import React, { useEffect, useMemo, useCallback, FC } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Tab, Segment, Menu, Icon } from "semantic-ui-react";
import DOMPurify from "dompurify";
import useHasCRUDAccess from "../../custom-hooks/useHasCRUDAccess";
import {
  fetchApps,
  fetchDeploymentTypes,
  setSelectedApp,
} from "../reducer/actions";
import { toggleSlidingMenu } from "../../actions/toggleSlidingMenu";
import { PageToolbar } from "../../components/PageToolbar";
import Breadcrumbs from "../../components/shared/breadcrumbs/Breadcrumbs";
import { MarketplaceAppItem, MarketplaceRouteParams } from "../types";
import { AppContent } from "./custom-components/types";
import MarketplaceAppDetailFeaturesTab from "./custom-components/MarketplaceAppDetailFeaturesTab";
import MarketplaceAppDetailResourcesTab from "./custom-components/MarketplaceAppDetailResourcesTab";
import MarketplaceAppDetailPricingTab from "./custom-components/MarketplaceAppDetailPricingTab";
import MarketplaceAppDetailServiceDetailsCard from "./custom-components/ MarketplaceAppDetailServiceDetailsCard";
import MarketplaceAppDetailSystemRequirements from "./custom-components/MarketplaceAppDetailSystemRequirementsTab";
import AppIcon from "../bits/AppIcon";
import { State } from "../../selectors/state";

const MarketplaceAppDetail: FC = () => {
  const { appName } = useParams<MarketplaceRouteParams>();
  const dispatch = useDispatch();

  const apps = useSelector((state: State) => state.marketplace.apps);
  const deploymentTypes = useSelector(
    (state: State) => state.marketplace.deploymentTypes,
  );
  const hasCRUDAccess = useHasCRUDAccess("openstack");

  const app = useMemo(() => {
    return apps.find(
      (a: MarketplaceAppItem) =>
        a.title.toLowerCase() === appName.toLowerCase(),
    );
  }, [apps, appName]);

  useEffect(() => {
    if (apps.length === 0) {
      dispatch(fetchApps());
    }
  }, [dispatch, apps.length]);

  useEffect(() => {
    if (app?.id) {
      dispatch(fetchDeploymentTypes(app.id));
    }
  }, [dispatch, app?.id]);

  const handleInstallClick = useCallback(() => {
    if (app) {
      dispatch(setSelectedApp(app));
      dispatch(toggleSlidingMenu("modify", "Marketplace", { app }));
    }
  }, [dispatch, app]);

  const handleContactUsClick = useCallback(() => {
    if (app) {
      dispatch(setSelectedApp(app));
      dispatch(toggleSlidingMenu("create", "Marketplace Contact Us", { app }));
    }
  }, [dispatch, app]);

  const breadcrumbs = useMemo(
    () => [
      { title: "Marketplace", pathname: "/marketplace" },
      { title: app?.title || "" },
    ],
    [app?.title],
  );

  const appContent: AppContent = useMemo(() => {
    try {
      return JSON.parse(app?.description || "{}");
    } catch (error) {
      console.error("Failed to parse app.description JSON:", error);
      return { tabs: [] };
    }
  }, [app?.description]);

  const customComponentsMap: { [key: string]: React.FC<any> } = useMemo(
    () => ({
      features: MarketplaceAppDetailFeaturesTab,
      resources: MarketplaceAppDetailResourcesTab,
      pricing: MarketplaceAppDetailPricingTab,
      system_requirements: MarketplaceAppDetailSystemRequirements,
    }),
    [],
  );

  const panes = useMemo(() => {
    return (appContent.tabs || []).map((tab: any, index: number) => {
      const { title, type, content, position, className } = tab;
      const CustomComponent = customComponentsMap[type];

      return {
        menuItem: (
          <Menu.Item
            key={index}
            position={position}
            className={className || ""}
          >
            <span>{title}</span>
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane key={index} className="padding-top-30 ">
            {CustomComponent ? (
              <CustomComponent {...content} />
            ) : (
              <div
                className="padding-left-20"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content || ""),
                }}
              />
            )}
          </Tab.Pane>
        ),
      };
    });
  }, [appContent.tabs, customComponentsMap]);

  if (!app) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <PageToolbar>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </PageToolbar>

      <div className="page-content marketplace-app-detail">
        <Grid>
          <Grid.Row>
            <Grid.Column
              textAlign="left"
              tablet={16}
              mobile={16}
              computer={16}
              widescreen={11}
              className="padding-left-00 padding-right-00"
            >
              <Segment className="padding-top-00">
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={12}>
                      <div className="flex padding-left-40">
                        <div className="logo">
                          <AppIcon title={app.title} size={75} />
                        </div>
                        <div className="flex-1 margin-left-half margin-top-half">
                          <h2 className="margin-bottom-00 font-L">
                            {app.title}
                          </h2>
                          <span className="">{app.serviceType}</span>
                        </div>
                      </div>
                    </Grid.Column>
                    <Grid.Column className="flex justify-content-end" width={4}>
                      {hasCRUDAccess && deploymentTypes.length > 0 && (
                        <button
                          className="button button--orange"
                          onClick={handleInstallClick}
                        >
                          <Icon name="rocket" className="padding-right-20" />
                          Deploy this App
                        </button>
                      )}
                      {hasCRUDAccess && deploymentTypes.length === 0 && (
                        <button
                          className="button button--orange"
                          onClick={handleContactUsClick}
                        >
                          <Icon name="mail" className="padding-right-20" />
                          Contact Cleura
                        </button>
                      )}

                      <button
                        className="button button--bordered margin-right-20 margin-left-10"
                        onClick={handleContactUsClick}
                      >
                        Contact Us
                      </button>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>

                <Tab
                  defaultActiveIndex={0}
                  className="horizontal-tabs width-100p with-padding padding-top-20 horizontal-tabs--white"
                  panes={panes}
                />
              </Segment>
            </Grid.Column>

            <Grid.Column
              computer={16}
              widescreen={5}
              tablet={16}
              mobile={16}
              className="width-100p padding-right-30"
            >
              {appContent.serviceDetails && (
                <MarketplaceAppDetailServiceDetailsCard
                  details={appContent.serviceDetails}
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </>
  );
};

export default MarketplaceAppDetail;
